<template>
  <div>
    <v-dialog v-model="detailDialog" max-width="600" content-class="glass-card">
      <v-card v-if="selectedDetailEntry" color="#1E1E1E" class="rounded-lg">
        <v-card-title class="d-flex justify-space-between align-start pb-0">
          <div class="d-flex align-center">
            <v-avatar :color="getIconColor(selectedDetailEntry.type)" size="48" class="mr-3" style="opacity: 0.9">
              <v-icon color="white">{{ getIcon(selectedDetailEntry.type) }}</v-icon>
            </v-avatar>
            <div>
              <div class="text-h6 white--text">{{ selectedDetailEntry.employee.fullName }}</div>
              <div class="text-caption grey--text text-uppercase font-weight-bold">
                {{
                  selectedDetailEntry.type === "holiday"
                    ? "Ferienantrag"
                    : selectedDetailEntry.type === "freewish"
                    ? "Freiwunsch"
                    : selectedDetailEntry.type === "shiftwish"
                    ? "Schichtwunsch"
                    : "Krankmeldung / Abwesenheit"
                }}
              </div>
            </div>
          </div>
          <v-btn icon @click="detailDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>

        <v-card-text class="pt-6">
          <div v-if="selectedDetailEntry.type === 'holiday'">
            <v-alert v-if="hasMissingFteError(selectedDetailEntry)" type="warning" dense outlined prominent border="left" class="mb-4" color="orange">
              <div class="font-weight-bold mb-1">Fehlende Stundenberechnung</div>
              <div class="text-caption" style="color: rgba(255, 255, 255, 0.7)">
                Für Arbeitstage sind <strong>0 Stunden</strong> hinterlegt. <br />
                Prüfe, ob Stellenprozente im Mitarbeiterprofil hinterlegt sind.
              </div>
            </v-alert>

            <div class="mb-2 white--text font-weight-bold">Tagesübersicht:</div>
            <v-simple-table dense class="transparent-table rounded-lg overflow-hidden border-light mb-4">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left grey--text">Datum</th>
                    <th class="text-left grey--text">Typ</th>
                    <th class="text-right grey--text">Dauer</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(day, index) in selectedDetailEntry.dates" :key="index">
                    <td class="white--text">{{ $moment(day.date).format("dd, DD.MM.YYYY") }}</td>
                    <td>
                      <span v-if="day.vacationDay" class="grey--text text-caption">{{ day.vacationDay }}</span>
                      <span v-else class="green--text text--lighten-2 text-caption font-weight-bold">Arbeitstag</span>
                    </td>
                    <td class="text-right">
                      <span v-if="day.vacationDay" class="grey--text">-</span>
                      <span v-else-if="!day.duration || day.duration === 0" class="red--text font-weight-bold">
                        0.0 h <v-icon x-small color="red">mdi-alert-circle</v-icon>
                      </span>
                      <span v-else class="white--text">{{ day.duration }} h</span>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>

          <div v-else-if="selectedDetailEntry.type === 'sick'">
            <v-sheet color="rgba(255,255,255,0.05)" class="rounded-lg pa-4 border-light mb-4">
              <v-row dense>
                <v-col cols="6">
                  <div class="text-caption grey--text mb-1">Grund</div>
                  <div class="text-h6 white--text">
                    {{ getEntryTitle(selectedDetailEntry) }}
                  </div>
                </v-col>
                <v-col cols="6">
                  <div class="text-caption grey--text mb-1">Gesamtstunden</div>
                  <div class="text-h6 white--text">{{ selectedDetailEntry.duration }} h</div>
                </v-col>
              </v-row>
            </v-sheet>

            <div class="mb-2 white--text font-weight-bold">Tagesübersicht:</div>
            <v-simple-table dense class="transparent-table rounded-lg overflow-hidden border-light mb-4">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left grey--text">Datum</th>
                    <th class="text-right grey--text">Sollstunden</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(day, index) in selectedDetailEntry.dates" :key="index">
                    <td class="white--text">{{ $moment(day.date).format("dd, DD.MM.YYYY") }}</td>
                    <td class="text-right white--text">{{ day.duration }} h</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>

            <div class="mt-4" v-if="selectedDetailEntry.employeeResponse">
              <div class="text-subtitle-2 white--text mb-2">Mitarbeiter Rückmeldung:</div>
              <v-alert
                :color="selectedDetailEntry.employeeResponse.confirmed ? 'rgba(0, 150, 63, 0.15)' : 'rgba(244, 67, 54, 0.15)'"
                class="rounded-lg text-left mb-4 border-light"
                dense>
                <div class="d-flex align-center">
                  <v-icon left :color="selectedDetailEntry.employeeResponse.confirmed ? 'success' : 'error'">
                    {{ selectedDetailEntry.employeeResponse.confirmed ? "mdi-check-circle" : "mdi-close-circle" }}
                  </v-icon>
                  <div>
                    <span class="font-weight-bold white--text">
                      {{
                        selectedDetailEntry.employeeResponse.confirmed
                          ? "Mitarbeiter hat Abwesenheit bestätigt"
                          : 'Mitarbeiter sagt: "Ich war nicht krank/abwesend"'
                      }}
                    </span>
                    <div v-if="selectedDetailEntry.employeeResponse.respondedAt" class="text-caption grey--text">
                      Datum: {{ $moment(selectedDetailEntry.employeeResponse.respondedAt).format("DD.MM.YYYY HH:mm") }}
                    </div>
                  </div>
                </div>
              </v-alert>

              <div v-if="selectedDetailEntry.employeeResponse.comment" class="grey--text text-caption pa-3 rounded border-light mb-4">
                <v-icon x-small class="mr-1">mdi-comment-text-outline</v-icon>
                <span class="font-italic">"{{ selectedDetailEntry.employeeResponse.comment }}"</span>
              </div>
            </div>

            <div v-if="selectedDetailEntry.attachment" class="mt-4">
              <div class="text-subtitle-2 white--text mb-2">Arztzeugnis (AUB):</div>
              <v-card color="rgba(255, 255, 255, 0.05)" class="rounded-lg pa-3 border-light d-flex align-center justify-space-between">
                <div class="d-flex align-center">
                  <v-icon color="#E53935" class="mr-3" large>
                    {{
                      selectedDetailEntry.attachment.mimeType && selectedDetailEntry.attachment.mimeType.includes("pdf")
                        ? "mdi-file-pdf-box"
                        : "mdi-file-image"
                    }}
                  </v-icon>
                  <div class="text-left">
                    <div class="white--text text-body-2 font-weight-bold text-truncate" style="max-width: 200px">
                      {{ selectedDetailEntry.attachment.originalName || "Arztzeugnis" }}
                    </div>
                    <div class="grey--text text-caption" v-if="selectedDetailEntry.attachment.size">
                      {{ (selectedDetailEntry.attachment.size / 1024 / 1024).toFixed(2) }} MB
                    </div>
                  </div>
                </div>
                <v-btn small color="#00963f" class="black--text font-weight-bold" @click="downloadAttachment(selectedDetailEntry)">
                  <v-icon left small>mdi-download</v-icon> Download
                </v-btn>
              </v-card>
            </div>
          </div>

          <div v-else class="d-flex flex-column gap-3">
            <v-sheet color="rgba(255,255,255,0.05)" class="rounded-lg pa-4 border-light">
              <v-row dense>
                <v-col cols="6">
                  <div class="text-caption grey--text mb-1">Datum</div>
                  <div class="text-h6 white--text">
                    <v-icon small color="grey" class="mr-1">mdi-calendar</v-icon>
                    {{ $moment(selectedDetailEntry.day).format("dd, DD.MM.YYYY") }}
                  </div>
                </v-col>
                <v-col cols="6">
                  <div class="text-caption grey--text mb-1">Detail</div>
                  <div class="text-h6 white--text">
                    {{ getEntryTitle(selectedDetailEntry) }}
                  </div>
                </v-col>
              </v-row>
            </v-sheet>
          </div>

          <div
            v-if="selectedDetailEntry.type !== 'sick' && (selectedDetailEntry.comment || selectedDetailEntry.reason)"
            class="grey--text text-caption pa-3 rounded border-light mt-3">
            <v-icon x-small class="mr-1">mdi-comment-text-outline</v-icon>
            <span class="font-italic">"{{ selectedDetailEntry.comment || selectedDetailEntry.reason }}"</span>
          </div>
        </v-card-text>

        <v-card-actions
          class="pa-4 pt-0 justify-end"
          v-if="
            selectedDetailEntry.status === 'open' || selectedDetailEntry.status === 'pending_employee' || selectedDetailEntry.status === 'pending_cl'
          ">
          <v-btn text color="red" @click="processRequest(selectedDetailEntry, 'rejected')">Ablehnen</v-btn>
          <v-btn color="#00963f" class="black--text elevation-0" @click="processRequest(selectedDetailEntry, 'accepted')">
            <v-icon left small>mdi-check</v-icon> Genehmigen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-card class="glass-card pa-4 mb-4 rounded-lg" elevation="0" color="#1E1E1E">
      <v-row dense align="center">
        <v-col cols="12" md="3">
          <v-btn-toggle v-model="statusFilter" mandatory dense class="glass-toggle rounded-lg w-100">
            <v-btn value="open" class="toggle-btn flex-grow-1" active-class="active-orange">
              <v-icon left small color="orange">mdi-inbox</v-icon> {{ stats.open }}
            </v-btn>
            <v-btn value="accepted" class="toggle-btn flex-grow-1" active-class="active-green">
              <v-icon left small color="#00963f">mdi-check</v-icon>
            </v-btn>
            <v-btn value="rejected" class="toggle-btn flex-grow-1" active-class="active-red">
              <v-icon left small color="red">mdi-close</v-icon>
            </v-btn>
          </v-btn-toggle>
        </v-col>

        <v-col cols="12" md="4" class="d-flex gap-2">
          <v-menu v-model="menuStart" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="formattedStartDate"
                label="Von"
                prepend-inner-icon="mdi-calendar"
                readonly
                dense
                outlined
                hide-details
                class="rounded-lg"
                v-bind="attrs"
                v-on="on"></v-text-field>
            </template>
            <v-date-picker v-model="startDate" no-title scrollable color="#00963f" @input="menuStart = false"></v-date-picker>
          </v-menu>

          <v-menu v-model="menuEnd" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="formattedEndDate"
                label="Bis"
                prepend-inner-icon="mdi-calendar"
                readonly
                dense
                outlined
                hide-details
                class="rounded-lg"
                v-bind="attrs"
                v-on="on"></v-text-field>
            </template>
            <v-date-picker v-model="endDate" no-title scrollable color="#00963f" @input="menuEnd = false"></v-date-picker>
          </v-menu>
        </v-col>

        <v-col cols="12" md="3">
          <v-autocomplete
            v-model="employeeFilter"
            :items="availableEmployees"
            item-text="fullName"
            item-value="_id"
            label="Mitarbeiter suchen"
            dense
            outlined
            hide-details
            clearable
            prepend-inner-icon="mdi-account-search"
            class="rounded-lg"></v-autocomplete>
        </v-col>

        <v-col cols="12" md="2" class="d-flex justify-end">
          <v-menu offset-y :close-on-content-click="false" left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on" class="mr-2">
                <v-icon>mdi-filter-variant</v-icon>
              </v-btn>
            </template>
            <v-card color="#252525" width="220">
              <v-list dark color="transparent" dense>
                <v-list-item v-for="type in typeOptions" :key="type.value">
                  <v-checkbox
                    v-model="typeFilter"
                    :value="type.value"
                    :label="type.text"
                    color="#00963f"
                    style="color: white"
                    hide-details></v-checkbox>
                </v-list-item>
              </v-list>
            </v-card>
          </v-menu>

          <v-btn-toggle v-model="viewMode" mandatory dense class="glass-toggle rounded-lg">
            <v-btn value="list">
              <v-icon small>mdi-format-list-bulleted</v-icon>
            </v-btn>
            <v-btn value="grouped">
              <v-icon small>mdi-account-group</v-icon>
            </v-btn>
          </v-btn-toggle>
        </v-col>
      </v-row>
    </v-card>

    <v-skeleton-loader v-if="loading" type="article, article, article" class="transparent"></v-skeleton-loader>

    <div v-else-if="filteredEntries.length === 0" class="text-center py-10 grey--text d-flex flex-column align-center">
      <v-icon size="64" color="#333" class="mb-4">mdi-calendar-remove</v-icon>
      <div>Keine Anträge im gewählten Zeitraum gefunden.</div>
    </div>

    <div v-else>
      <div v-if="viewMode === 'list'" class="d-flex flex-column gap-3 mt-2">
        <v-slide-y-transition group>
          <v-card
            v-for="entry in sortedEntries"
            :key="entry._id"
            class="rounded-lg pa-0 d-flex align-center border-light card-hover-lift"
            color="#252525"
            elevation="0"
            @click="openDetails(entry)"
            style="cursor: pointer">
            <div class="status-stripe" :class="[entry.status, { open: entry.status === 'pending_employee' || entry.status === 'pending_cl' }]"></div>

            <div class="pa-4 d-flex align-center justify-center" style="width: 80px">
              <v-sheet
                :color="getIconColor(entry.type)"
                class="rounded-lg d-flex align-center justify-center"
                width="45"
                height="45"
                style="opacity: 0.9">
                <v-icon color="white">{{ getIcon(entry.type) }}</v-icon>
              </v-sheet>
            </div>

            <div class="flex-grow-1 py-3 pr-4 overflow-hidden">
              <div class="d-flex justify-space-between align-start">
                <div>
                  <div class="text-subtitle-1 font-weight-bold white--text mb-0">
                    {{ entry.employee.fullName }}
                    <v-chip class="ml-3" x-small label>{{ "Antrag gestellt am: " + $moment(entry.createdAt).format("DD.MM.YYYY") }}</v-chip>
                  </div>

                  <div class="d-flex align-center mt-1">
                    <v-icon x-small color="grey" class="mr-1">mdi-calendar</v-icon>
                    <span class="white--text font-weight-medium">{{ getDateString(entry) }}</span>
                    <v-chip x-small label class="ml-2 font-weight-bold" :color="getChipColor(entry)">{{ getEntryTitle(entry) }}</v-chip>
                  </div>
                </div>

                <div v-if="entry.status === 'open' || entry.status === 'pending_employee' || entry.status === 'pending_cl'" class="d-flex">
                  <v-btn icon class="mr-1 green-hover" @click.stop="processRequest(entry, 'accepted')"><v-icon>mdi-check</v-icon></v-btn>
                  <v-btn icon class="red-hover" @click.stop="processRequest(entry, 'rejected')"><v-icon>mdi-close</v-icon></v-btn>
                </div>
                <div v-else>
                  <v-chip small outlined :color="entry.status === 'accepted' ? 'green' : 'red'">{{
                    entry.status === "accepted" ? "Genehmigt" : "Abgelehnt"
                  }}</v-chip>
                  <v-btn v-if="entry.status === 'accepted'" icon small color="grey" class="ml-2" @click.stop="processRequest(entry, 'rejected')"
                    ><v-icon small>mdi-undo</v-icon></v-btn
                  >
                </div>
              </div>

              <div class="mt-2 text-caption grey--text" v-if="entry.comment || entry.reason">
                <v-icon x-small color="grey" class="mr-1">mdi-comment-text-outline</v-icon>
                {{ entry.comment || entry.reason }}
              </div>

              <div v-if="hasMissingFteError(entry)" class="mt-1 text-caption red--text font-weight-bold">
                <v-icon x-small color="red" class="mr-1">mdi-alert</v-icon> Fehler: 0 Stunden berechnet (Stellenprozente fehlen)
              </div>
              <div v-else-if="entry.type === 'holiday' && entry.status === 'open'" class="mt-1 text-caption orange--text">
                <v-icon x-small color="orange" class="mr-1">mdi-alert-circle-outline</v-icon> Resturlaub:
                {{ Math.round(entry.vacationDaysLeft * 100) / 100 }} Tage
              </div>
            </div>
          </v-card>
        </v-slide-y-transition>
      </div>

      <div v-else class="mt-2">
        <v-expansion-panels multiple accordion class="transparent-panels">
          <v-expansion-panel v-for="(group, employeeId) in groupedEntries" :key="employeeId" class="mb-2 rounded-lg border-light transparent-bg">
            <v-expansion-panel-header color="#252525">
              <div class="d-flex align-center">
                <v-avatar size="32" color="rgba(255,255,255,0.1)" class="mr-3">
                  <span class="white--text font-weight-bold">{{ group.name.charAt(0) }}</span>
                </v-avatar>
                <div class="font-weight-bold white--text text-subtitle-1">{{ group.name }}</div>
                <v-spacer></v-spacer>
                <div class="d-flex mr-4">
                  <v-chip x-small v-if="group.counts.holiday" color="amber darken-3" class="mr-1 white--text">
                    {{ group.counts.holiday }}x Ferien
                  </v-chip>
                  <v-chip x-small v-if="group.counts.freewish" color="purple darken-3" class="mr-1 white--text">
                    {{ group.counts.freewish }}x Frei
                  </v-chip>
                  <v-chip x-small v-if="group.counts.shiftwish" color="blue darken-3" class="mr-1 white--text">
                    {{ group.counts.shiftwish }}x Schicht
                  </v-chip>
                  <v-chip x-small v-if="group.counts.sick" color="red darken-3" class="white--text"> {{ group.counts.sick }}x Krank </v-chip>
                </div>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content color="#1e1e1e" class="pt-4">
              <div class="d-flex flex-column gap-3">
                <v-card
                  v-for="entry in group.entries"
                  :key="entry._id"
                  class="rounded-lg pa-0 d-flex align-center border-light card-hover-lift"
                  color="#252525"
                  elevation="0"
                  @click="openDetails(entry)"
                  style="cursor: pointer">
                  <div
                    class="status-stripe"
                    :class="[entry.status, { open: entry.status === 'pending_employee' || entry.status === 'pending_cl' }]"></div>

                  <div class="pa-4 d-flex align-center justify-center" style="width: 80px">
                    <v-sheet
                      :color="getIconColor(entry.type)"
                      class="rounded-lg d-flex align-center justify-center"
                      width="45"
                      height="45"
                      style="opacity: 0.9">
                      <v-icon color="white">{{ getIcon(entry.type) }}</v-icon>
                    </v-sheet>
                  </div>

                  <div class="flex-grow-1 py-3 pr-4 overflow-hidden">
                    <div class="d-flex justify-space-between align-start">
                      <div>
                        <div class="d-flex align-center">
                          <v-icon x-small color="grey" class="mr-1">mdi-calendar</v-icon>
                          <span class="white--text font-weight-medium">{{ getDateString(entry) }}</span>
                          <v-chip x-small label class="ml-2 font-weight-bold" :color="getChipColor(entry)">{{ getEntryTitle(entry) }}</v-chip>
                        </div>
                      </div>

                      <div v-if="entry.status === 'open' || entry.status === 'pending_employee' || entry.status === 'pending_cl'" class="d-flex">
                        <v-btn icon class="mr-1 green-hover" @click.stop="processRequest(entry, 'accepted')"><v-icon>mdi-check</v-icon></v-btn>
                        <v-btn icon class="red-hover" @click.stop="processRequest(entry, 'rejected')"><v-icon>mdi-close</v-icon></v-btn>
                      </div>
                      <div v-else>
                        <v-chip small outlined :color="entry.status === 'accepted' ? 'green' : 'red'">{{
                          entry.status === "accepted" ? "Genehmigt" : "Abgelehnt"
                        }}</v-chip>
                        <v-btn v-if="entry.status === 'accepted'" icon small color="grey" class="ml-2" @click.stop="processRequest(entry, 'rejected')"
                          ><v-icon small>mdi-undo</v-icon></v-btn
                        >
                      </div>
                    </div>

                    <div class="mt-2 text-caption grey--text" v-if="entry.comment || entry.reason">
                      <v-icon x-small color="grey" class="mr-1">mdi-comment-text-outline</v-icon>
                      {{ entry.comment || entry.reason }}
                    </div>

                    <div v-if="hasMissingFteError(entry)" class="mt-1 text-caption red--text font-weight-bold">
                      <v-icon x-small color="red" class="mr-1">mdi-alert</v-icon> Fehler: 0 Stunden berechnet
                    </div>
                    <div v-else-if="entry.type === 'holiday' && entry.status === 'open'" class="mt-1 text-caption orange--text">
                      <v-icon x-small color="orange" class="mr-1">mdi-alert-circle-outline</v-icon> Resturlaub:
                      {{ Math.round(entry.vacationDaysLeft * 100) / 100 }} Tage
                    </div>
                  </div>
                </v-card>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/api"
import axios from "axios"

export default {
  name: "ManagementRequestsView",
  props: {
    selectedStudioSingle: { required: true },
  },
  data() {
    return {
      loading: false,
      entries: [],
      statusFilter: "open",
      employeeFilter: null,
      viewMode: "list",
      typeFilter: ["holiday", "freewish", "shiftwish"],
      typeOptions: [
        { text: "Ferien", value: "holiday" },
        { text: "Freiwünsche", value: "freewish" },
        { text: "Schichtwünsche", value: "shiftwish" },
      ],
      // Date Picker Data
      menuStart: false,
      menuEnd: false,
      startDate: this.$moment().startOf("year").format("YYYY-MM-DD"),
      endDate: this.$moment().add(3, "months").endOf("month").format("YYYY-MM-DD"),

      // DETAIL VIEW DATEN
      detailDialog: false,
      selectedDetailEntry: null,
    }
  },
  computed: {
    formattedStartDate() {
      return this.$moment(this.startDate).format("DD.MM.YYYY")
    },
    formattedEndDate() {
      return this.$moment(this.endDate).format("DD.MM.YYYY")
    },
    stats() {
      return { open: this.entries.filter((e) => e.status === "open" || e.status === "pending_employee" || e.status === "pending_cl").length }
    },
    availableEmployees() {
      const emps = new Map()
      this.entries.forEach((entry) => {
        if (entry.employee && entry.employee._id) emps.set(entry.employee._id, entry.employee)
      })
      return Array.from(emps.values()).sort((a, b) => {
        const nameA = a.fullName || `${a.vorname || ""} ${a.nachname || ""}`.trim() || "Unbekannter Mitarbeiter"
        const nameB = b.fullName || `${b.vorname || ""} ${b.nachname || ""}`.trim() || "Unbekannter Mitarbeiter"
        return nameA.localeCompare(nameB)
      })
    },

    // FILTER LOGIK
    filteredEntries() {
      return this.entries.filter((entry) => {
        // 1. Status & Typ
        if (this.statusFilter === "open") {
          if (entry.status !== "open" && entry.status !== "pending_employee" && entry.status !== "pending_cl") return false
        } else {
          if (entry.status !== this.statusFilter) return false
        }
        if (!this.typeFilter.includes(entry.type)) return false

        // 2. Mitarbeiter
        if (this.employeeFilter && entry.employee._id !== this.employeeFilter) return false

        // 3. Datum Overlap Check
        return this.isOverlapping(entry)
      })
    },

    sortedEntries() {
      return [...this.filteredEntries].sort((a, b) => {
        const dateA = this.getSortDate(a)
        const dateB = this.getSortDate(b)
        return dateB - dateA
      })
    },

    groupedEntries() {
      const groups = {}
      this.sortedEntries.forEach((entry) => {
        const uid = entry.employee._id
        if (!groups[uid]) {
          groups[uid] = { name: entry.employee.fullName, entries: [], counts: { holiday: 0, freewish: 0, shiftwish: 0, sick: 0 } }
        }
        groups[uid].entries.push(entry)
        groups[uid].counts[entry.type]++
      })
      return groups
    },
  },
  watch: {
    selectedStudioSingle: {
      handler() {
        this.fetchEntries()
      },
      immediate: true,
    },
    statusFilter() {
      this.fetchEntries()
    },
    startDate() {
      this.fetchEntries()
    },
    endDate() {
      this.fetchEntries()
    },
  },
  methods: {
    // DETAIL LOGIK
    openDetails(entry) {
      this.selectedDetailEntry = entry
      this.detailDialog = true
    },
    hasMissingFteError(entry) {
      if (!entry.dates || !Array.isArray(entry.dates)) return false
      // Ein Fehler liegt vor, wenn es KEIN Feiertag/Wochenende ist (vacationDay == false)
      // ABER die duration null oder 0 ist.
      return entry.dates.some((d) => d.vacationDay === false && (!d.duration || d.duration === 0))
    },

    getSortDate(entry) {
      if (entry.dates && entry.dates.length) return new Date(entry.dates[0].date)
      return new Date(entry.day)
    },
    isOverlapping(entry) {
      let entryStart, entryEnd
      if ((entry.type === "holiday" || entry.type === "sick") && entry.dates && entry.dates.length) {
        entryStart = this.$moment(entry.dates[0].date)
        entryEnd = this.$moment(entry.dates[entry.dates.length - 1].date)
      } else {
        entryStart = this.$moment(entry.day)
        entryEnd = this.$moment(entry.day)
      }
      const filterStart = this.$moment(this.startDate)
      const filterEnd = this.$moment(this.endDate)
      return entryStart.isSameOrBefore(filterEnd, "day") && entryEnd.isSameOrAfter(filterStart, "day")
    },

    async fetchEntries() {
      if (!this.selectedStudioSingle || this.selectedStudioSingle === "undefined" || this.selectedStudioSingle === "null") return
      this.loading = true
      try {
        this.entries = await api.fetchManagementRequests(this.selectedStudioSingle, this.statusFilter, this.startDate, this.endDate)
        if (this.statusFilter === "open") {
          this.$emit("updateCount", this.entries.length)
        } else {
          this.$emit("updateCount")
        }
      } catch (e) {
        console.log(e)
        this.$toast.error("Fehler beim Laden")
      } finally {
        this.loading = false
      }
    },
    async processRequest(entry, newStatus) {
      if (!confirm(newStatus === "accepted" ? "Genehmigen?" : "Ablehnen?")) return
      try {
        if (entry.type === "holiday") await api.changeHolidayStatus({ _id: entry._id, status: newStatus })
        else if (entry.type === "freewish") await api.changeFreeWishStatus({ _id: entry._id, status: newStatus })
        else if (entry.type === "shiftwish") await api.changeShiftWishStatus({ _id: entry._id, status: newStatus })
        else if (entry.type === "sick") {
          await api.confirmSickEntry(entry._id, { confirmed: newStatus === "accepted" })
        }

        this.$toast.success("Gespeichert")
        this.detailDialog = false // Schließe Dialog nach Aktion
        this.fetchEntries()
      } catch (e) {
        this.$toast.error("Fehler")
      }
    },
    getIcon(type) {
      if (type === "holiday") return "mdi-palm-tree"
      if (type === "freewish") return "mdi-calendar-heart"
      if (type === "shiftwish") return "mdi-clock-start"
      if (type === "sick") return "mdi-emoticon-sick-outline"
      return "mdi-file"
    },
    getIconColor(type) {
      if (type === "holiday") return "#FFC107"
      if (type === "freewish") return "#9C27B0"
      if (type === "shiftwish") return "#2196F3"
      if (type === "sick") return "#E53935"
      return "grey"
    },
    getEntryTitle(entry) {
      if (entry.type === "holiday") return `${entry.duration || 0} Tage Urlaub`
      if (entry.type === "freewish")
        return `Freiwunsch ${entry.period === "full" ? "Ganzer Tag" : entry.period === "morning" ? "Vormittag" : "Nachmittag"}`
      if (entry.type === "shiftwish") {
        const types = { morning: "Frühschicht", intermediate: "Zwischenschicht", late: "Spätschicht" }
        return `Wunsch: ${types[entry.shiftType] || "Schicht"}`
      }
      if (entry.type === "sick") {
        const reasons = {
          sick: "Krank",
          school: "Schule",
          military: "Militär",
          pregnancy: "Schwangerschaft",
          other: "Anderes",
        }
        return `Abwesenheit: ${reasons[entry.reason] || entry.reason || "Krank"}`
      }
      return "Antrag"
    },
    getChipColor(entry) {
      if (entry.type === "sick") return "rgba(229, 57, 53, 0.15)"
      return "rgba(255,255,255,0.1)"
    },
    getDateString(entry) {
      if ((entry.type === "holiday" || entry.type === "sick") && entry.dates && entry.dates.length > 0) {
        return `${this.$moment(entry.dates[0].date).format("DD.MM.")} - ${this.$moment(entry.dates[entry.dates.length - 1].date).format("DD.MM.YY")}`
      }
      return this.$moment(entry.day).format("dd, DD.MM.YYYY")
    },
    async downloadAttachment(entry) {
      try {
        const url = `/api/timetracker/sick/${entry._id}/attachment`
        const response = await axios.get(url, {
          headers: {
            Authorization: localStorage.getItem("token"),
          },
          responseType: "blob",
        })
        const blob = new Blob([response.data], { type: entry.attachment.mimeType })
        const downloadUrl = window.URL.createObjectURL(blob)
        const link = document.createElement("a")
        link.href = downloadUrl
        link.setAttribute("download", entry.attachment.originalName || "arztzeugnis")
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(downloadUrl)
      } catch (err) {
        console.error("Error downloading attachment:", err)
        this.$toast.error("Fehler beim Herunterladen des Arztzeugnisses")
      }
    },
  },
}
</script>

<style scoped lang="scss">
.gap-2 {
  gap: 8px;
}
.gap-3 {
  gap: 12px;
}
.glass-toggle {
  background: rgba(255, 255, 255, 0.05) !important;
  border: 1px solid rgba(255, 255, 255, 0.05);
}
.toggle-btn {
  background: transparent !important;
  border: none !important;
  color: grey !important;
  &.active-orange {
    color: #ff9800 !important;
    background: rgba(255, 152, 0, 0.1) !important;
  }
  &.active-green {
    color: #00963f !important;
    background: rgba(0, 150, 63, 0.1) !important;
  }
  &.active-red {
    color: #f44336 !important;
    background: rgba(244, 67, 54, 0.1) !important;
  }
}
.border-light {
  border: 1px solid rgba(255, 255, 255, 0.05) !important;
}
.transparent-panels {
  background: transparent !important;
  .v-expansion-panel {
    background-color: transparent !important;
    &::before {
      box-shadow: none !important;
    }
  }
}
.card-hover-lift {
  transition: all 0.2s;
  &:hover {
    transform: translateY(-2px);
    background-color: rgba(50, 50, 50, 1) !important;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  }
}
.status-stripe {
  width: 4px;
  height: 100%;
  min-height: 85px;
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
  position: absolute;
  left: 0;
  top: 0;
  &.open {
    background-color: #ff9800;
  }
  &.accepted {
    background-color: #00963f;
  }
  &.rejected {
    background-color: #f44336;
  }
}
.green-hover:hover {
  color: #00963f;
  background: rgba(0, 150, 63, 0.1);
}
.red-hover:hover {
  color: #f44336;
  background: rgba(244, 67, 54, 0.1);
}
/* Styles für Tabelle */
.transparent-table {
  background-color: rgba(255, 255, 255, 0.02) !important;
}
.theme--light.v-data-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr:hover:not(.v-data-table__expanded__content):not(.v-data-table__empty-wrapper) {
  background: rgba(255, 255, 255, 0.05) !important;
}
</style>
